import { jwtDecode } from 'jwt-decode';

/**
 * The claims EBP reads off a Descope session token.
 *
 * `email`, `name` and `medplumId` are put there by the sign-in flow's "Custom
 * Claims" action; `permissions` is what Descope derives from the user's roles
 * in the Clinical project; `sub` is the Descope user id.
 */
export interface DescopeSessionClaims {
  sub?: unknown;
  email?: unknown;
  name?: unknown;
  medplumId?: unknown;
  permissions?: unknown;
}

/**
 * The claims carried by a Descope session token, or `undefined` when there is
 * no readable token.
 *
 * The web reads the session claims this way rather than from the Descope React
 * SDK's `useSession().claims`. That object is NOT the token's claims: the SDK
 * publishes only the `claims` field an auth RESPONSE body carries, which holds
 * neither the role-derived `permissions` nor the flow's `name`, and is absent
 * altogether until a response arrives - a reload restores the token from
 * storage without one. The token is what the API validates and authorizes on,
 * so reading it keeps the UI's view of the session identical to the API's.
 *
 * Decoding is not verification, and it is not meant to be: a client cannot
 * verify anything. Everything these claims gate is enforced again on the
 * server - the API validates the same token on every request, and the security
 * rules gate Firestore and Storage on the token minted from it.
 */
export const readDescopeSessionClaims = (
  token: unknown,
): DescopeSessionClaims | undefined => {
  if (typeof token !== 'string' || !token) {
    return undefined;
  }

  try {
    const claims = jwtDecode<DescopeSessionClaims>(token);

    // A JWT payload may legally be any JSON value; only an object carries
    // claims. `jwtDecode` returns null-prototype objects, so no instanceof.
    return typeof claims === 'object' &&
      claims !== null &&
      !Array.isArray(claims)
      ? claims
      : undefined;
  } catch {
    // Malformed, truncated or otherwise undecodable: no claims, which every
    // caller treats as "not signed in yet".
    return undefined;
  }
};
